import { z } from "zod";

import { cancellationReasonSchema } from "@/features/billing/models";
import type { Subscription } from "@/features/billing/models";
import type { SubmitFormParams as SubmitHealthieFormParams } from "@/features/intake-form/api";
import { parseFormFields } from "@/features/intake-form/api";
import type { Product } from "@/features/products/models";
import { parseResponseData, privateAPI } from "@/shared/lib";
import type { ServiceResponse } from "@/shared/lib/axios";
import { refillTypeSchema } from "@/shared/models";
import { FORM_TYPES, formSchema } from "../models";
import type { Answers, Form } from "../models";

export const getAvailableRefillTypes = async (
  subscriptionId: Subscription["id"],
) => {
  const { data } = await privateAPI.get<ServiceResponse<unknown>>(
    `/patients/subscriptions/${subscriptionId}/available-refill-types`,
  );

  return z.array(refillTypeSchema).parse(data.data);
};

export const getMicrodosingCheckInForm = async (productId: Product["id"]) => {
  const response = await privateAPI.get(`/forms/check-in`, {
    params: { productId },
  });

  return parseResponseData(response, formSchema);
};

export const submitMicrodosingCheckInForm = async ({
  answers,
  ...params
}: SubmitHealthieFormParams) => {
  const { data } = await privateAPI.post<ServiceResponse<unknown>>(
    "forms/check-in",
    {
      ...params,
      answers: parseFormFields(answers),
    },
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  return data.data;
};

export const updateFollowUpStatus = async (
  subscriptionId: Subscription["id"],
) => {
  const { data } = await privateAPI.patch<ServiceResponse<unknown>>(
    `patients/subscriptions/${subscriptionId}/dismiss-follow-up`,
  );

  return data.data;
};

export const getCancellationReasons = async () => {
  const { data } = await privateAPI.get<ServiceResponse<unknown>>(
    "patients/subscriptions/cancellation-reasons",
  );

  return z.array(cancellationReasonSchema).parse(data.data);
};

export const getDietitianForm = async () => {
  const response = await privateAPI.get(`/forms/v2/${FORM_TYPES.DIETITIAN}`);

  return parseResponseData(response, formSchema);
};

interface SubmitFormParams {
  formId: Form["formId"];
  answers: Answers;
}

export const submitDietitianForm = async ({
  answers,
  ...params
}: SubmitFormParams) => {
  await privateAPI.post("/forms/v2", {
    ...params,
    answers: parseFormFields(answers),
  });
};
